/*** Swarm RTS Game module - (c) mjh.at - v0.3.1 2014-12-28 ***/

import { GridSystem, EntitySystem, PerformanceOverlay, EventFeed, Util } from './util.js';
import { EffectSystem } from './effects.js';
import { PLANET_OPTIONS, computeCaptureRadius, initEntityModule, createSpawner, createSpawnerHQ,
    createClaimable, createTechBoostPickup, createTechGiftPickup, createTextTriggerPickup,
    BONUS_SHIP_FACTORIES, drainEntityTimings } from './entities.js';
import { Faction, PLAYABLE_FACTION_IDS, FACTION_NAMES, FACTION_COLORS, factionId, factionFromId } from './factions.js';
import { createDefaultPowers } from './powers.js';
import { getAllLevels, getDefaultLevelId, getCachedLevel, getLevelById } from './levels.js';
import { HeuristicAi } from './heuristic_ai.js';
import { NeuralNetAi } from './nn_ai.js';
import { getPreferredFrameRate } from './platform.js';
import { hasAiChoice, getModelUrls, getModelVersionFromChoiceId } from './ai_model_catalog.js';
import { loadModel as loadModelInWorker, onModelLoaded } from './nn_worker_client.js';
import { STATE_ENCODER_VERSION_V2 } from './state_encoder.js';
import { createRenderer } from './render/factory.js';
// WebGLRenderer is no longer imported here — use renderer.type to distinguish backends.
import { initImageCache } from './image_cache.js';
import { UI } from './ui.js';
import { TechTreeManager } from './tech_tree.js';
import { soundManager, Sfx } from './sound.js';
import { ScoreTracker } from './scoring.js';
import { gameEventLog } from './logging.js';
import { getStorage } from './storage.js';
import { getEmpCharges, consumeEmpCharge, tryAwardEmp, tryGrantEmpOnEntry } from './powerup_inventory.js';
import { trackGameStarted, trackGameEnded, trackGameAbandoned } from './analytics.js';
import { cgGameplayStart, cgGameplayStop } from './external_integrations.js';
// LevelEditor is imported dynamically in toggleEditor() to avoid DOM access in headless/Node mode

function normalizeFactionRecord(input) {
    const normalized = {};
    if (!input) return normalized;
    for (const [key, value] of Object.entries(input)) {
        normalized[factionId(key)] = value;
    }
    return normalized;
}

function getFactionName(faction) {
    return factionFromId(faction).name;
}

const SIMULATION_SPEED_MODES = [0.25, 0.5, 1, 2, 3, 4];

const LOW_FPS_THRESHOLD = 30;
const LOW_FPS_CONSECUTIVE_SECONDS = 3;
/** Ratio of a faction's strength to the strongest enemy below which a warning fires. */
const NEARLY_DEFEATED_RATIO = 0.12;
/** Ratio above which the warning is cleared (hysteresis). */
const NEARLY_DEFEATED_RECOVER_RATIO = 0.20;
const MIN_SPAWN_RATE_MULTIPLIER = 0.0;
const SPAWN_RATE_ADJUST_STEP = 0.1;
const BREAKOUT_TILE_SIZE = 10;
const BREAKOUT_AI_LOCK_SECONDS = 5;
const BREAKOUT_COLLISIONS_TO_BREAK = 40;
const BREAKOUT_BOUNCE_NOISE_RADIANS = Math.PI / 10; // +/-18 degrees
const GAME_STATE_LEVEL_COMPLETE = 200;
const POST_GAME_SETTLE_SEC = 2.0;
/**
 * How many frames a freshly rebuilt renderer keeps repainting the game world.
 * One is not enough for the WebGL backend: its canvas is created and inserted
 * in the same turn, and the first frame it presents can be dropped (no
 * compositor layer yet, and the drawing buffer is not preserved), while a
 * canvas size sync right after the rebuild clears it again. A paused game
 * marks nothing else dirty, so that lone frame would be all the world ever
 * got — the blank GPU view that only came back on resume. Counted in frames
 * rather than milliseconds: a throttled tab can burn a time budget on a
 * single frame, which is exactly the case this has to survive.
 */
const RENDERER_REBUILD_REDRAW_FRAMES = 12;

/**
 * Main game controller
 */
/**
 * Main game controller
 */
class Game {
    /** @type {GridSystem} */
    locationGrid;
    /** @type {GridSystem} */
    destinationGrid;
    /** @type {EntitySystem} */
    entitySystem;
    /** @type {EffectSystem} */
    effectSystem;
    /** @type {Entity[]} */
    entities;
    /** @type {Renderer|null} */
    renderer;
    /** @type {UI|null} */
    ui;

    /** @type {number} 0 = init, 1 = running, 10x = faction x has won, 200 = level complete */
    gameState;
    /** @type {Set<number>} Factions whose defeat we have already announced. */
    _defeatedAnnounced = new Set();
    /** @type {Set<number>} Factions currently in "nearly defeated" warning state. */
    _factionWarned = new Set();
    /** @type {number} */
    lastFps = 1;
    /** @type {boolean} */
    DEBUG = false;
    /** @type {boolean} */
    headless = false;

    /** @type {number} */
    frameCounter = 0;
    /** @type {number} */
    instantFps = 60;
    /** @type {number} */
    smoothedFps = 60;
    /** @type {number} */
    time = 0;
    
    // Performance timing (in milliseconds, smoothed)
    /** @type {number} */
    aiTimeMs = 0;
    /** @type {number} */
    gameLogicTimeMs = 0;
    /** @type {number} */
    renderTimeMs = 0;
    /** @type {number} */
    techTreeTimeMs = 0;
    /** @type {number} */
    entitiesTimeMs = 0;
    /** @type {number} */
    spawnersTimeMs = 0;
    /** @type {number} */
    reserveDestCalls = 0;  // calls to reserveDestination() per tick (integer counter, cheap)
    /** @type {number} */
    captureCheckMs = 0;    // time spent in CaptureComponent.checkCaptureStatus() per tick
    /** @type {number} */
    otherTimeMs = 0;
    /** @type {{[key: string]: number}} */
    entityTypeTimesMs = {};
    // Fighter-specific timing breakdown
    /** @type {number} */
    fighterSetupTimeMs = 0;
    /** @type {number} */
    fighterMovementTimeMs = 0;
    /** @type {number} */
    fighterCombatTimeMs = 0;
    /** @type {number} */
    fighterOtherTimeMs = 0;
    /** @type {number} */
    effectsTimeMs = 0;
    /** @type {number} Smoothed EMA of time spent in renderEntities() per frame (ms). */
    entityRenderTimeMs = 0;
    /** @type {number} */
    aiDetermineStrategyMs = 0;
    /** @type {number} */
    aiSendUnitsMs = 0;
    // Raw (unsmoothed) — shows actual spike values in the overlay.
    /** @type {number} */ aiTimeRawMs = 0;
    /** @type {number} */ aiStrategicRawMs = 0;
    /** @type {number} */ aiSendUnitsRawMs = 0;
    /** @type {Record<number, {faction: number, stratMs: number, sendMs: number}>} */
    aiPerFactionRaw = {};
    
    /** @type {number} */
    lowFpsStreakSeconds = 0;
    /** @type {string} */
    selectedPowerUp = "turncoat_ray";
    /** @type {Power[]} */
    powers = [];
    /** @type {number} */
    simulationSpeedMode = 1;
    /** @type {number} */
    renderEveryNFrames = 1;
    /** @type {number} */
    renderFrameCounter = 0;
    /** @type {number} Frames of unconditional world repaint still owed to a
     *  freshly built renderer — see RENDERER_REBUILD_REDRAW_FRAMES. */
    _worldRedrawFrames = 0;
    /** @type {number} */
    pendingRenderTime = 0;
    /** @type {Map<string, {x: number, y: number}>} */
    breakoutTiles = new Map();
    /** @type {string} */
    levelId = getDefaultLevelId();
    /** @type {any} */
    level = null;
    /** @type {number} */
    levelTimeLimitSec = 0;
    /** @type {number} */
    levelZoomScale = 1;
    /** @type {Map<string, number|null>} */
    powerUseLimits = new Map();
    /** @type {Map<string, number>} */
    powerSecsPerCharge = new Map();
    /** @type {Map<string, number>} */
    powerMaxStoredUses = new Map();
    /** @type {number} */
    levelElapsedSec = 0;
    /** @type {boolean} */
    aiDebugMode = false;
    /** @type {{[key: number]: string}} Per-faction AI type: 'rule' | 'neural' */
    aiTypes = {};
    /** @type {{[key: number]: any}} Per-faction TF.js LayersModel for headless/sync inference */
    aiModels = {};
    /** @type {{[key: number]: string}} Per-faction AI choice id for settings UI */
    aiChoiceIds = {};
    /** @type {object | null} */
    editor = null;

    /**
     * @param {object} [options] - Configuration options
     * @param {boolean} [options.headless=false] - Run without rendering/UI
     * @param {number} [options.width=1200] - Game world width
     * @param {number} [options.height=680] - Game world height
     * @param {number} [options.gridSize=20] - Grid cell size
     * @param {object} [options.level] - Inline level config (bypasses file-based lookup)
     * @param {{[key: number]: string}} [options.aiTypes] - Per-faction AI type, e.g. {1: 'rule', 2: 'neural'}
     * @param {{[key: number]: any}} [options.aiModels] - Per-faction pre-loaded TF.js model (headless/training only)
     * @param {{[key: number]: string}} [options.aiChoiceIds] - Per-faction chooser ids (heuristic, v2_alpha, v1_alpha, ...)
     */
    constructor(options = {}) {
        this.headless = options.headless || false;
        this.width = options.width || 1200;
        this.height = options.height || 680;
        this.gridSize = options.gridSize || 20;
        /** Optional callback invoked at the end of every successful `loadLevel()`. @type {((levelId: string) => void) | null} */
        this.onLevelLoaded = null;
        if (options.level) {
            this.level = options.level;
            this.levelId = options.level.id || 'inline';
        } else {
            this.levelId = options.levelId || getDefaultLevelId();
            this.level = getCachedLevel(this.levelId);
        }
        this.aiTypes = normalizeFactionRecord(options.aiTypes);
        this.aiModels = normalizeFactionRecord(options.aiModels);
        this.aiChoiceIds = normalizeFactionRecord(options.aiChoiceIds);

        // Surface-dependent, so the platform layer owns the rule. The measured
        // display rate and then the saved setting override it from here.
        this.targetFps = getPreferredFrameRate();

        /** @type {number[]} Raw rAF inter-frame gaps for display Hz detection. */
        this._rafRateAccum = [];
        /** @type {boolean} True once display Hz has been auto-detected. */
        this._rafRateDetected = false;
        /** @type {number} Previous raw rAF timestamp for gap measurement. */
        this._rafPrevTimestampRaw = 0;
        /** @type {number|null} Detected display refresh rate in Hz, or null if not yet known. */
        this.detectedDisplayHz = null;

        /** @type {'auto'|'high'|'low'} Graphics quality mode. */
        this.graphicsQuality = 'auto';
        /** @type {number} Steps below native DPR in adaptive auto mode (0 = native). */
        this._adaptiveDprSteps = 0;
        /** @type {number} Consecutive low-fps FPS windows (used for adaptive hysteresis). */
        this._lowDprFrameWindows = 0;
        /** @type {number} Consecutive high-fps FPS windows (used for adaptive hysteresis). */
        this._highDprFrameWindows = 0;
        
        // Initialize event feed first — _resetLevelState() (below) builds the
        // grids/entity systems and needs it in place.
        this.eventFeed = new EventFeed();

        /**
         * Active tutorial overlay messages, shown below the timer or as speech bubbles.
         * Each entry expires independently.
         * @type {Array<{text: string, color: string, speechBubble: boolean, worldX?: number, worldY?: number, expiresAt: number}>}
         */
        this.tutorialMessages = [];

        /** @type {boolean} Whether TTS voice announcements are muted. */
        this.voiceMuted = true;
        /** @type {number} TTS volume 0–1. */
        this.voiceVolume = 0.4;
        /** @type {string|null} Selected voice name, or null for auto-pick. */
        this.voiceName = null;

        if (!this.headless && typeof window !== 'undefined') {
            const _origAddEvent = this.eventFeed.addEvent.bind(this.eventFeed);

            if (window.AndroidTTS) {
                // Native Android TTS bridge — reliable in WebView where speechSynthesis is flaky.
                const ANDROID_PREFERRED_VOICES = ['en-us-x-tpc-local'];
                /** @param {string} message @param {number} [vol] */
                this._speakDirect = (message, vol) => {
                    if (this.voiceMuted) return;
                    // Lazy auto-selection on first speak (TTS engine may not be ready at construction).
                    if (this.voiceName === null) {
                        const available = this.getAvailableVoices();
                        const pick = ANDROID_PREFERRED_VOICES.reduce(
                            (found, id) => found ?? available.find(v => v.id === id) ?? null, null
                        );
                        if (pick) this.voiceName = pick.id;
                    }
                    window.AndroidTTS.speak(message, this.voiceName ?? '', vol ?? this.voiceVolume, 1.1);
                };
                this.eventFeed.addEvent = (message, color) => {
                    _origAddEvent(message, color);
                    this._speakDirect(message);
                };
            } else if (typeof window.speechSynthesis?.speak === 'function'
                       && typeof window.SpeechSynthesisUtterance === 'function') {
                // Some WebViews (iOS 15 Google app, for one) expose a partial
                // speechSynthesis with no addEventListener/getVoices; touching
                // those threw during construction and took the whole boot down
                // with it (Sentry LORD-OF-THE-SWARM-G). Voice is a nicety —
                // every call below is guarded so a thin implementation just
                // means no voice, not no game.
                const synth = window.speechSynthesis;
                /** @returns {SpeechSynthesisVoice[]} */
                const getVoices = () => {
                    try { return synth.getVoices?.() ?? []; } catch { return []; }
                };
                /** @type {SpeechSynthesisVoice|null} */
                let _autoVoice = null;

                const pickAutoVoice = () => {
                    const voices = getVoices();
                    if (!voices.length) return;
                    // Curated list — first available match wins.
                    const PREFERRED_VOICES = [
                        'Fred',
                    ];
                    const byName = (name) => voices.find(v => v.name === name);
                    const preferred = PREFERRED_VOICES.reduce((found, name) => found ?? byName(name), null);
                    if (preferred) { _autoVoice = preferred; return; }
                    // Fallback: enhanced/premium English, then Google, then any local English.
                    const enVoices = voices.filter(v => v.lang.startsWith('en'));
                    _autoVoice =
                        enVoices.find(v => /enhanced|premium/i.test(v.name))
                        ?? enVoices.find(v => /google/i.test(v.name))
                        ?? enVoices.find(v => v.localService)
                        ?? enVoices[0]
                        ?? voices[0];
                };

                pickAutoVoice();
                if (typeof synth.addEventListener === 'function') {
                    synth.addEventListener('voiceschanged', pickAutoVoice);
                } else {
                    synth.onvoiceschanged = pickAutoVoice;
                }

                /** @param {string} message @param {number} [vol] */
                this._speakDirect = (message, vol) => {
                    if (this.voiceMuted) return;
                    try {
                        const u = new SpeechSynthesisUtterance(message);
                        const voices = getVoices();
                        u.voice = (this.voiceName
                            ? voices.find(v => v.name === this.voiceName)
                            : null) ?? _autoVoice;
                        u.rate = 1.1;
                        u.volume = vol ?? this.voiceVolume;
                        synth.speak(u);
                    } catch (err) {
                        console.warn('[voice] speechSynthesis.speak failed:', err);
                    }
                };
                this.eventFeed.addEvent = (message, color) => {
                    _origAddEvent(message, color);
                    this._speakDirect(message);
                };
            }
        }
        
        // Initialize performance overlay
        this.performanceOverlay = new PerformanceOverlay();
        
        // The shared SoundManager singleton (see sound.js). Kept as a property
        // for UI/debug convenience (game.soundManager in the console). It stays
        // disabled — a safe no-op — until a user gesture enables + unlocks it,
        // so headless games can call through it freely.
        this.soundManager = soundManager;

        this._resetLevelState();

        // Only set up rendering/UI if not headless
        if (!this.headless) {
            initImageCache({ onImageLoaded: () => this.markGameLayerDirty() });
            this.ui = new UI(this);
            const canvases = {
                bg: document.getElementById('bgCanvas'),
                fg: document.getElementById('fgCanvas'),
                ui: document.getElementById('uiCanvas'),
                effects: document.getElementById('effectsCanvas')
            };
            this.renderer = createRenderer(canvases, this, this.ui, this.eventFeed, this.performanceOverlay);
            this.ui.setGameSize(this.locationGrid.width, this.locationGrid.height);
            this.ui.addEventListeners(); // after soundManager so loadSettings can restore audio prefs
        } else {
            this.ui = null;
            this.renderer = null;
        }
        
        this._startLevel();

        // If a neural model fails to load in the worker, the faction goes idle —
        // no silent fallback to heuristic, so the failure is obvious.
        if (!this.headless) {
            onModelLoaded((faction, success) => {
                if (success) return;
                console.error(`[AI] ✖ Failed to load model for ${getFactionName(faction)} (${this.aiChoiceIds[faction]}) — faction will be idle.`);
                this.entitySystem.computerAis[faction] = null;
                this._renderDirty = true;
            });
        }
    }

    /**
     * Create AI controllers for each faction based on aiTypes config.
     * - Browser (non-headless): NeuralNetAi for neural factions, dispatches inference to worker.
     * - Headless + model provided: NeuralNetAi in sync mode (training/evaluation scripts).
     * - Headless + no model: HeuristicAi (game mechanic tests that don't exercise strategic AI).
     */
    initializeAis() {
        for (const faction of PLAYABLE_FACTION_IDS) {
            const hasSpawners = this.entitySystem.spawners.some(
                s => s.capturedByFaction === faction
            );
            if (!hasSpawners) continue;

            const aiType = this.aiTypes[faction] || 'neural';
            const syncModel = this.aiModels[faction] || null;

            if (aiType === 'neural' && (!this.headless || syncModel)) {
                const choiceId = this.aiChoiceIds[faction] || 'v2_alpha';
                const modelVersion = getModelVersionFromChoiceId(choiceId) || STATE_ENCODER_VERSION_V2;
                this.entitySystem.computerAis[faction] = new NeuralNetAi(
                    faction,
                    this.entitySystem,
                    modelVersion,
                    this.locationGrid,
                    this.locationGrid.width,
                    this.locationGrid.height,
                    syncModel
                );
                if (!syncModel) {
                    // Without this the worker has no model and every inference
                    // fails silently, leaving the faction idle. The client
                    // dedupes repeat loads across level reloads.
                    const urls = getModelUrls(choiceId);
                    if (urls?.length) loadModelInWorker(faction, urls);
                }
                gameEventLog(`[AI] ${getFactionName(faction)}: Neural Network`);
            } else {
                this.entitySystem.computerAis[faction] = new HeuristicAi(faction, this.entitySystem, this.locationGrid);
                gameEventLog(`[AI] ${getFactionName(faction)}: Rule-based Heuristic`);
            }
            if (!this.aiChoiceIds[faction]) {
                this.aiChoiceIds[faction] = (aiType === 'neural' && (!this.headless || syncModel))
                    ? 'v2_alpha' : 'heuristic';
            }
        }
    }

    getFactionAiChoiceId(faction) {
        const id = this.aiChoiceIds[factionId(faction)];
        if (hasAiChoice(id)) return id;
        return 'heuristic';
    }

    setFactionAiChoice(faction, choiceId) {
        const f = factionId(faction);
        this.aiChoiceIds[f] = hasAiChoice(choiceId) ? choiceId : 'heuristic';
        const hasSpawners = this.entitySystem.spawners.some((s) => s.capturedByFaction === f);
        if (!hasSpawners) return false;

        const modelVersion = getModelVersionFromChoiceId(this.aiChoiceIds[f]);
        if (modelVersion) {
            // Neural: create the AI immediately; model loads async in worker.
            this.aiTypes[f] = 'neural';
            this.entitySystem.computerAis[f] = new NeuralNetAi(
                f,
                this.entitySystem,
                modelVersion,
                this.locationGrid,
                this.locationGrid.width,
                this.locationGrid.height
            );
            this.entitySystem.computerAis[f].init();
            gameEventLog(`[AI] ${getFactionName(f)}: Neural (${this.aiChoiceIds[f]})`);

            // Fire off model load in worker; fall back to heuristic if it fails.
            loadModelInWorker(f, getModelUrls(this.aiChoiceIds[f]));
        } else {
            this.aiTypes[f] = 'rule';
            this.entitySystem.computerAis[f] = new HeuristicAi(f, this.entitySystem, this.locationGrid);
            this.entitySystem.computerAis[f].init();
            gameEventLog(`[AI] ${getFactionName(f)}: Rule-based Heuristic`);
        }
        this._renderDirty = true;
        this._needsInitialRender = true;
        return true;
    }

    beginPostGameSettle() {
        this.postGameSettleUntil = Math.max(this.postGameSettleUntil, this.time + POST_GAME_SETTLE_SEC);
        this._needsInitialRender = true;
        this._renderDirty = true;
    }

    /**
     * @param {number} nowSec
     * @returns {boolean}
     */
    isPostGameSettling(nowSec) {
        if (this.gameState === 1 || this.gameState === 0) {
            return false;
        }
        return nowSec < this.postGameSettleUntil;
    }



    /* Step 1 in game loop: check game state */
    checkGameState() {
        // Only detect new win/loss transitions during active play.
        // Otherwise post-game settle would be re-triggered every tick.
        if (this.gameState !== 1 || this.gameState === GAME_STATE_LEVEL_COMPLETE) {
            return;
        }
        let factionsInPlay = 0;
        let lastFactionInPlay = Faction.NEUTRAL;

        for (let f = 1; f < this.entitySystem.unitCount.length; f += 1) {
            const hasCombatUnits = this.entitySystem.unitCount[f] > 0;
            const hasOwnedSpawner = this.entitySystem.spawnerCount[f] > 0;
            if (hasCombatUnits || hasOwnedSpawner) {
                factionsInPlay += 1;
                lastFactionInPlay = f;
            }
        }


        if (factionsInPlay <= 1) {
            this.gameState = 100 + lastFactionInPlay;
            this.beginPostGameSettle();
            this._saveScore();
            if (this.eventFeed) {
                const name = FACTION_NAMES[lastFactionInPlay] ?? `Faction ${lastFactionInPlay}`;
                this.eventFeed.addEvent(`${name} won, you lose.`, FACTION_COLORS[lastFactionInPlay]);
            }
            this.soundManager.stopMusic();
            // One faction eliminated the other before the time limit — player lost balance.
            this.soundManager.playSfx(Sfx.GAME_OVER);
        }
    }

    /* Update tech tree system */
    updateTechTree(timePassed) {
        if (this.techTreeManager) {
            this.techTreeManager.update(timePassed);
        }
    }

    /**
     * @param {number} timePassed
     */
    updateLevelProgress(timePassed) {
        if (this.levelTimeLimitSec <= 0 || this.gameState !== 1) {
            return;
        }
        this.levelElapsedSec += timePassed;
        if (this.levelElapsedSec >= this.levelTimeLimitSec) {
            this.levelElapsedSec = this.levelTimeLimitSec;
            this.gameState = GAME_STATE_LEVEL_COMPLETE;
            this.beginPostGameSettle();
            this._saveScore();
            if (this.eventFeed) {
                this.eventFeed.addEvent('Level complete!', '#9AF59A');
            }
            this.soundManager.stopMusic();
            this.soundManager.playSfx(Sfx.GAME_WON);
        }
    }

    /* Step 3 in game loop: populate update to all entities */
    /**
     * @param {number} timePassed
     */
    updateGameState(timePassed) {
        var s = 0, e = 0, a, entity;
        
        // Track setup time
        const setupStart = performance.now();
        this.entitySystem.advanceTime(timePassed);
        this.entitySystem.pruneExpiredSpeedZones();
        this.entitySystem.clearSpeedZoneSectorCache();
        this.entitySystem.clearTargetSearchCache();  // Clear sector-level target search cache
        const setupEnd = performance.now();

        // Entity counts by type — used for display only, almost zero overhead.
        const entityTypeCounts = {};

        // Randomize unit update direction each tick to avoid first-mover combat bias
        const entitiesStart = performance.now();
        if (Math.random() < 0.5) {
            while (e < this.entitySystem.units.length) {
                entity = this.entitySystem.units[e];
                if (entity.isDestroyed()) {
                    this.entitySystem.removeUnitByIndex(e);
                } else {
                    entity.update(timePassed);
                    this.resolveBreakoutBlockCollision(entity);
                    const typeName = entity.type || 'unknown';
                    entityTypeCounts[typeName] = (entityTypeCounts[typeName] || 0) + 1;
                    e += 1;
                }
            }
        } else {
            e = this.entitySystem.units.length - 1;
            while (e >= 0) {
                entity = this.entitySystem.units[e];
                if (entity.isDestroyed()) {
                    this.entitySystem.removeUnitByIndex(e);
                } else {
                    entity.update(timePassed);
                    this.resolveBreakoutBlockCollision(entity);
                    const typeName = entity.type || 'unknown';
                    entityTypeCounts[typeName] = (entityTypeCounts[typeName] || 0) + 1;
                }
                e -= 1;
            }
        }
        const entitiesEnd = performance.now();

        // Track spawner updates
        const spawnersStart = performance.now();
        while (s < this.entitySystem.spawners.length) {
            this.entitySystem.spawners[s].update(timePassed);
            s += 1;
        }
        const spawnersEnd = performance.now();

        // Track slow zone updates
        const speedZonesStart = performance.now();
        for (let i = 0; i < this.entitySystem.speedZones.length; i++) {
            this.entitySystem.speedZones[i].update(timePassed);
        }
        const speedZonesEnd = performance.now();

        // Track AI updates
        const aiStart = performance.now();
        let aiDetermineStrategyTime = 0;
        let aiSendUnitsTime = 0;
        
        // Randomize AI update order each tick to avoid first-mover bias.
        // Skip eliminated factions — they have no units or spawners left to command.
        const aiOrder = [];
        for (let i = 0; i < PLAYABLE_FACTION_IDS.length; i += 1) {
            a = PLAYABLE_FACTION_IDS[i];
            if (this.entitySystem.computerAis[a] !== null &&
                !this.entitySystem.eliminatedFactions.has(a)) aiOrder.push(a);
        }
        for (a = aiOrder.length - 1; a > 0; a -= 1) {
            const j = Math.floor(Math.random() * (a + 1));
            const tmp = aiOrder[a]; aiOrder[a] = aiOrder[j]; aiOrder[j] = tmp;
        }
        for (a = 0; a < aiOrder.length; a += 1) {
            const ai = this.entitySystem.computerAis[aiOrder[a]];
            ai.update(timePassed);
            
            // Accumulate AI sub-timings
            aiDetermineStrategyTime += ai.lastStrategicPlanTime || 0;
            aiSendUnitsTime += ai.lastSendUnitsTime || 0;
        }
        const aiEnd = performance.now();

        // Per-faction raw AI breakdown — update each faction's entry individually
        // so staggered plan cycles don't overwrite each other.
        for (a = 0; a < aiOrder.length; a += 1) {
            const ai = this.entitySystem.computerAis[aiOrder[a]];
            const stratMs = ai.lastStrategicPlanTime || 0;
            const sendMs  = ai.lastSendUnitsTime    || 0;
            if (stratMs + sendMs > 0) {
                this.aiPerFactionRaw[aiOrder[a]] = {
                    faction: aiOrder[a],
                    stratMs,
                    sendMs,
                    aiType: this.entitySystem.computerAis[aiOrder[a]].aiTypeName,
                };
            }
        }
        
        // Drain per-tick entity sub-timings (zeroes the accumulators for next tick).
        const { reserveDestCalls, captureCheckMs, findTargetMs } = drainEntityTimings();

        // Smooth all timings with exponential moving average
        const setupTime = setupEnd - setupStart;
        const entitiesTime = entitiesEnd - entitiesStart;
        const spawnersTime = spawnersEnd - spawnersStart;
        const speedZonesTime = speedZonesEnd - speedZonesStart;
        const aiTime = aiEnd - aiStart;
        const otherTime = setupTime;
        
        this.otherTimeMs = this.otherTimeMs * 0.9 + otherTime * 0.1;
        this.entitiesTimeMs = this.entitiesTimeMs * 0.9 + entitiesTime * 0.1;
        this.spawnersTimeMs = this.spawnersTimeMs * 0.9 + spawnersTime * 0.1;
        this.reserveDestCalls = this.reserveDestCalls * 0.9 + reserveDestCalls * 0.1;
        this.captureCheckMs   = this.captureCheckMs   * 0.9 + captureCheckMs   * 0.1;
        this.findTargetMs     = (this.findTargetMs || 0) * 0.9 + findTargetMs * 0.1;
        this.speedZonesTimeMs = (this.speedZonesTimeMs || 0) * 0.9 + speedZonesTime * 0.1;
        this.aiTimeMs = this.aiTimeMs * 0.9 + aiTime * 0.1;

        // Raw (unsmoothed) AI totals — only overwritten when a plan actually ran,
        // so the overlay holds the last spike value between plan cycles.
        if (aiTime > 0.5) {
            this.aiTimeRawMs      = aiTime;
            this.aiStrategicRawMs = aiDetermineStrategyTime;
            this.aiSendUnitsRawMs = aiSendUnitsTime;
        }

        // Keep smoothed sub-component times for any other consumers.
        this.aiDetermineStrategyMs = this.aiDetermineStrategyMs * 0.9 + aiDetermineStrategyTime * 0.1;
        this.aiSendUnitsMs = this.aiSendUnitsMs * 0.9 + aiSendUnitsTime * 0.1;
        
        // Smooth entity type counts (replaces per-entity performance.now timing).
        for (const typeName in entityTypeCounts) {
            if (!this.entityTypeTimesMs[typeName]) {
                this.entityTypeTimesMs[typeName] = 0;
            }
            // Store smoothed count so the overlay can display e.g. "Fighter: 3821"
            this.entityTypeTimesMs[typeName] = this.entityTypeTimesMs[typeName] * 0.9 + entityTypeCounts[typeName] * 0.1;
        }
    }

    /* Main game loop */
    gameLoop(rafTimestamp = 0) {
        // Measure raw rAF delivery rate to detect ProMotion (120Hz) before any
        // frame-skipping. This runs on every rAF callback regardless of our targetFps.
        if (rafTimestamp > 0 && this._rafPrevTimestampRaw > 0) {
            const gap = rafTimestamp - this._rafPrevTimestampRaw;
            if (gap > 0 && gap < 20) this._rafRateAccum.push(gap);
            if (this._rafRateAccum.length >= 30 && !this._rafRateDetected) {
                const avg = this._rafRateAccum.reduce((a, b) => a + b, 0) / this._rafRateAccum.length;
                this.detectedDisplayHz = Math.round(1000 / avg);
                this._rafRateDetected = true;
                // Auto-upgrade targetFps if display is running faster than our current cap
                if (this.detectedDisplayHz > this.targetFps + 20) {
                    const snap = [30, 60, 90, 120, 144, 240];
                    this.targetFps = snap.reduce((prev, v) => Math.abs(v - this.detectedDisplayHz) < Math.abs(prev - this.detectedDisplayHz) ? v : prev);
                }
            }
        }
        if (rafTimestamp > 0) this._rafPrevTimestampRaw = rafTimestamp;

        const TARGET_FRAME_MS = 1000 / this.targetFps;
        const now = performance.now();
        if (now - this._lastFrameTime < TARGET_FRAME_MS - 1) {
            window.requestAnimationFrame((ts) => this.gameLoop(ts));
            return;
        }
        this._lastFrameTime = now;

        // Jank detection: track the gap between rAF callbacks.
        // A frame gap > 2× the target interval is a jank frame.
        if (this._rafPrevTimestamp > 0 && rafTimestamp > 0) {
            const gap = rafTimestamp - this._rafPrevTimestamp;
            if (gap > this.peakFrameGapMs) this.peakFrameGapMs = gap;
            if (gap > TARGET_FRAME_MS * 2) this.jankFrameCount += 1;
        }
        if (rafTimestamp > 0) this._rafPrevTimestamp = rafTimestamp;

        // Measured FPS: count actual work frames (after the throttle check) in a 1-second window.
        // Counting all rAF callbacks (including skipped ones) inflates measuredFps when
        // --disable-frame-rate-limit or a high-Hz display causes rAF to fire faster than targetFps.
        this._fpsWindowFrames += 1;
        if (rafTimestamp > 0) {
            if (this._fpsWindowStart === 0) this._fpsWindowStart = rafTimestamp;
            const elapsed = rafTimestamp - this._fpsWindowStart;
            if (elapsed >= 1000) {
                this.measuredFps = Math.round(this._fpsWindowFrames * 1000 / elapsed);
                this._fpsWindowFrames = 0;
                this._fpsWindowStart = rafTimestamp;

                // Adaptive DPR: step between native, native-1, …, 1x based on FPS.
                if (this.graphicsQuality === 'auto') {
                    const nativeDpr = (typeof devicePixelRatio !== 'undefined') ? Math.min(devicePixelRatio, 3) : 1;
                    if (nativeDpr > 1) {
                        const LOW_THRESHOLD  = this.targetFps * 0.82;
                        const HIGH_THRESHOLD = this.targetFps * 0.95;
                        const canStepDown = this._adaptiveDprSteps < nativeDpr - 1;
                        const canStepUp   = this._adaptiveDprSteps > 0;
                        if (canStepDown && this.measuredFps < LOW_THRESHOLD) {
                            this._lowDprFrameWindows++;
                            this._highDprFrameWindows = 0;
                            if (this._lowDprFrameWindows >= 2) {
                                this._adaptiveDprSteps++;
                                this._lowDprFrameWindows = 0;
                                this._triggerCanvasResize();
                            }
                        } else if (canStepUp && this.measuredFps >= HIGH_THRESHOLD) {
                            this._highDprFrameWindows++;
                            this._lowDprFrameWindows = 0;
                            if (this._highDprFrameWindows >= 4) {
                                this._adaptiveDprSteps--;
                                this._highDprFrameWindows = 0;
                                this._triggerCanvasResize();
                            }
                        } else {
                            this._lowDprFrameWindows = 0;
                            this._highDprFrameWindows = 0;
                        }
                    }
                }
            }
        }

        // Reset jank peak every 5 seconds so it reflects recent behaviour.
        if (rafTimestamp > 0) {
            if (this._jankWindowStart === 0) this._jankWindowStart = rafTimestamp;
            if (rafTimestamp - this._jankWindowStart >= 5000) {
                this.peakFrameGapMs = 0;
                this.jankFrameCount = 0;
                this._jankWindowStart = rafTimestamp;
            }
        }

        const simNow = this.timestamp();
        const timePassed = Math.min(1, simNow - this.time);
        
        // For speedup modes (2x, 4x), run multiple ticks with normal dt
        // For slowdown (0.5x), scale dt instead
        let ticksToRun, dtPerTick;
        
        if (this.simulationSpeedMode >= 1) {
            ticksToRun = Math.round(this.simulationSpeedMode);
            dtPerTick = timePassed;
        } else {
            ticksToRun = 1;
            dtPerTick = timePassed * this.simulationSpeedMode;
        }
        
        // Run simulation steps
        for (let i = 0; i < ticksToRun; i++) {
            this.tick(dtPerTick);
        }
        
        this.pendingRenderTime += timePassed * this.simulationSpeedMode;
        
        const editorActive = this.editor && this.editor.active;
        const isSimulating = this.gameState === 1;
        const isSettling = this.isPostGameSettling(simNow);
        if (editorActive && this.renderer) {
            // Run the active renderer so the editor preview shows real planets/WebGL.
            // updatePreviewPositions() syncs stub x/y in place so dragged items move
            // without invalidating the WebGL texture/pool cache.
            this.editor.updatePreviewPositions();
            this.renderer.renderBackground(0);
            this.renderer.renderGameLayer(0);
            // renderUiLayer is intentionally skipped — the editor pauses the game,
            // so the HUD timer/score would be misleading.  The editor draws its own
            // chrome in renderFrame() below.
            this.editor.renderFrame();
        } else {
            // A just-rebuilt renderer repaints the world for several frames
            // rather than the single one _needsInitialRender would give it.
            const settlingRenderer = this._worldRedrawFrames > 0;
            const shouldRender = isSimulating || isSettling || this._needsInitialRender
                || settlingRenderer || this._renderDirty;
            if (this.renderer && shouldRender &&
                this.renderFrameCounter % this.renderEveryNFrames === 0) {
                const renderStart = performance.now();
                if (isSimulating || isSettling || this._needsInitialRender || settlingRenderer) {
                    const simTime = (isSimulating || isSettling) ? this.pendingRenderTime : 0;
                    if (this._needsInitialRender || settlingRenderer || this.renderFrameCounter % 5 === 0) {
                        this.renderer.renderBackground(simTime);
                    }
                    this.renderer.renderGameLayer(simTime);
                    if (this._needsInitialRender) this._needsInitialRender = false;
                    if (this._worldRedrawFrames > 0) this._worldRedrawFrames--;
                }
                this.renderer.renderUiLayer();
                const renderEnd = performance.now();
                this.renderTimeMs = this.renderTimeMs * 0.9 + (renderEnd - renderStart) * 0.1;
                this.pendingRenderTime = 0;
                if (!isSimulating && !isSettling) this._renderDirty = false;
            }
        }

        this.renderFrameCounter += 1;
        this.frameCounter += 1;
        
        const frameTime = simNow - this.time;
        if (frameTime > 0) {
            this.instantFps = 1.0 / frameTime;
            this.smoothedFps = this.smoothedFps * 0.9 + this.instantFps * 0.1;
            this.lastFps = Math.round(this.smoothedFps);
        }
        
        this.time = simNow;
        
        // Schedule next frame AFTER doing the work
        window.requestAnimationFrame((ts) => this.gameLoop(ts));
    }
    
    /**
     * Advance the game simulation by one step
     * @param {number} timePassed - Time elapsed in seconds
     */
    tick(timePassed) {
        this.checkGameState();
        this.handleLossRestart();
        const isSettling = this.isPostGameSettling(this.time + timePassed);

        if (this.gameState === 1 || isSettling) {
            const gameLogicStart = performance.now();
            
            if (this.gameState === 1) {
                const techTreeStart = performance.now();
                this.updateTechTree(timePassed);  // Tech tree system
                const techTreeEnd = performance.now();
                this.techTreeTimeMs = this.techTreeTimeMs * 0.9 + (techTreeEnd - techTreeStart) * 0.1;
            }
            
            this.updateGameState(timePassed);
            if (this.gameState === 1) {
                this.updatePowerRecharges(timePassed);
                // Update score before checking level progress so _saveScore() captures
                // the current tick's contribution (avoids saved score being 1 behind display).
                this.scoreTracker.update(timePassed, this.entitySystem);
                this.updateLevelProgress(timePassed);
            }
            
            const gameLogicEnd = performance.now();
            this.gameLogicTimeMs = this.gameLogicTimeMs * 0.9 + (gameLogicEnd - gameLogicStart) * 0.1;
            
            this.soundManager.tick(timePassed * 1000);
            this._updateMusicIntensity();
        }
    }

    _updateMusicIntensity() {
        const counts = this.entitySystem.unitCount;
        let max = 0, min = Infinity, total = 0, active = 0;
        for (let f = 1; f < counts.length; f++) {
            const c = counts[f] + this.entitySystem.spawnerCount[f] * 10;
            if (c > 0) {
                if (c > max) max = c;
                if (c < min) min = c;
                total += c;
                active++;
            }
        }
        if (active <= 1 || total === 0) {
            this.soundManager.setIntensity(0.9);
            return;
        }
        // Imbalance ratio: 0 = perfectly balanced, 1 = totally lopsided
        const imbalance = 1 - (min / max);
        this.soundManager.setIntensity(0.15 + imbalance * 0.85);
    }


    /**
     * @returns {number} Current timestamp in seconds
     */
    timestamp() {
        return (window.performance && window.performance.now ?
            window.performance.now() : Date.now()) / 1000;
    }

    /* Entry point to start the game loop (browser mode only) */
    start() {
        if (this.headless) {
            throw new Error('Cannot call start() in headless mode. Use tick() to advance simulation.');
        }
        // start() is reachable from every splash button and the Space key; a
        // second call would run two rAF loops (double simulation speed) and
        // leak an extra interval.
        if (this._started) {
            return;
        }
        this._started = true;

        // Check adaptive spawn rate and faction status every second
        this._statusInterval = setInterval(() => {
            this.updateAdaptiveSpawnRate();
            this._checkFactionStatus();
        }, 1000);

        this.time = this.timestamp();
        this._lastFrameTime = 0;
        this._rafPrevTimestamp = 0;
        this.peakFrameGapMs = 0;
        this.jankFrameCount = 0;
        this._jankWindowStart = 0;
        this._fpsWindowStart = 0;
        this._fpsWindowFrames = 0;
        this.measuredFps = 0;
        window.requestAnimationFrame(() => this.gameLoop());
    }

    /**
     * Lower spawn rates when FPS has been low for several seconds.
     */
    updateAdaptiveSpawnRate() {
        if (this.lastFps < LOW_FPS_THRESHOLD) {
            this.lowFpsStreakSeconds += 1;
        } else {
            this.lowFpsStreakSeconds = 0;
        }

        if (this.lowFpsStreakSeconds >= LOW_FPS_CONSECUTIVE_SECONDS) {
            this.entitySystem.spawnRateMultiplier = Math.max(
                MIN_SPAWN_RATE_MULTIPLIER,
                this.entitySystem.spawnRateMultiplier - SPAWN_RATE_ADJUST_STEP
            );
        } else if (this.lastFps >= LOW_FPS_THRESHOLD) {
            this.entitySystem.spawnRateMultiplier = Math.min(
                1.0,
                this.entitySystem.spawnRateMultiplier + SPAWN_RATE_ADJUST_STEP
            );
        }
    }

    /**
     * Announce faction defeats and near-defeat warnings via the event feed.
     * Called once per second from the interval set up in start().
     *
     * "Nearly defeated" is defined as a faction whose military strength
     * (ships + spawners × 10) is below NEARLY_DEFEATED_RATIO of the strongest
     * active enemy faction's strength. The threshold is the same for any number
     * of players (2–6) because it is always relative to the single strongest enemy.
     * A faction that recovers above NEARLY_DEFEATED_RECOVER_RATIO is un-warned.
     */
    _checkFactionStatus() {
        if (this.gameState !== 1) return;

        const es = this.entitySystem;
        const feed = es.eventFeed;

        // Build strength map: ships count 1, spawners count 10 (they produce units).
        const SPAWNER_WEIGHT = 10;
        /** @type {Map<number, number>} */
        const strength = new Map();
        for (const fid of PLAYABLE_FACTION_IDS) {
            if (es.eliminatedFactions.has(fid)) continue;
            if ((es.unitCount[fid] ?? 0) === 0 && (es.spawnerCount[fid] ?? 0) === 0) continue;
            strength.set(fid, (es.unitCount[fid] ?? 0) + (es.spawnerCount[fid] ?? 0) * SPAWNER_WEIGHT);
        }

        // Warn factions that are critically outnumbered.
        for (const [fid, myStrength] of strength) {
            // Max strength of all active enemies.
            let maxEnemy = 0;
            for (const [eid, es2] of strength) {
                if (eid !== fid && es2 > maxEnemy) maxEnemy = es2;
            }
            if (maxEnemy === 0) continue;

            const ratio = myStrength / maxEnemy;
            const name = FACTION_NAMES[fid] ?? `Faction ${fid}`;

            if (!this._factionWarned.has(fid) && ratio < NEARLY_DEFEATED_RATIO) {
                this._factionWarned.add(fid);
                feed?.addEvent(`${name} is losing, help them!`, FACTION_COLORS[fid]);
            } else if (this._factionWarned.has(fid) && ratio >= NEARLY_DEFEATED_RECOVER_RATIO) {
                this._factionWarned.delete(fid);
            }
        }
    }

    /* Game state modification */
    /**
     */
    startGame() {
        if (this.gameState === 0) {
            this.postGameSettleUntil = 0;
            this.gameState = 1;
            this._everStarted = true;
            // Flush any deferred initial units now that the game has begun.
            for (const spawner of this.entitySystem.spawners) {
                if (spawner._pendingInitialUnits > 0) {
                    spawner.spawnUnits(spawner._pendingInitialUnits);
                    spawner._pendingInitialUnits = 0;
                }
            }
            if (!this.headless) {
                trackGameStarted(this.levelId);
                this._analyticsGameOpen = true;
            }
            if (this.eventFeed && this.level?.name) {
                this.eventFeed.addEvent(`Level: ${this.level.name}`, '#FFFF00');
            }
            this.soundManager.playSfx(Sfx.LEVEL_START);
            this.soundManager.startMusic();
        }
    }

    pauseGame() {
        // The portal wants a stop on every break, not just level end — its
        // playtime metric is measured between start/stop pairs. Only a running
        // game counts: pauseGame() is also called on level load and after the
        // game has ended, where there is no gameplay to interrupt.
        if (!this.headless && this.gameState === 1) cgGameplayStop();
        this._everStarted = this._everStarted || this.gameState === 1;
        this.gameState = 0;
        this._needsInitialRender = true;
        this._renderDirty = true;
        this.soundManager.pause();
    }

    resumeGame() {
        if (this.gameState !== 0 || !this._everStarted) return;
        if (!this.headless) cgGameplayStart(this.levelId);
        this.gameState = 1;
        this._needsInitialRender = true;
        this._renderDirty = true;
        this.soundManager.resume();
    }

    hasStarted() {
        return !!this._everStarted;
    }

    markRenderDirty() {
        this._renderDirty = true;
    }

    markGameLayerDirty() {
        this._renderDirty = true;
        this._needsInitialRender = true;
    }

    async toggleEditor() {
        // __ENABLE_EDITOR__ is replaced at build time (false in production bundles,
        // undefined in unbundled dev mode — the typeof guard treats undefined as enabled).
        if (typeof __ENABLE_EDITOR__ !== 'undefined' && !__ENABLE_EDITOR__) return;
        if (!this.editor) {
            const { LevelEditor } = await import('./editor.js');
            this.editor = new LevelEditor(this);
        }
        if (this.editor.active) {
            await this.editor.exit();
            this._needsInitialRender = true;
        } else {
            this.editor.enter();
        }
    }

    /**
     * @returns {boolean}
     */
    handleLossRestart() {
        if (this.gameState < 100 || this.gameState >= 200) {
            return false;
        }
        const winner = this.gameState - 100;
        if (winner === Faction.NEUTRAL) {
            this.gameState = GAME_STATE_LEVEL_COMPLETE;
            this.beginPostGameSettle();
            return true;
        }
        // Red or Blue victory counts as a level loss screen.
        return false;
    }

    /**
     * @returns {boolean}
     */
    canRestartLevel() {
        if (this.gameState < 100 || this.gameState >= 200) {
            return false;
        }
        const winner = this.gameState - 100;
        return winner !== Faction.NEUTRAL;
    }

    /**
     * Restart the current level at any time (paused, mid-game, or end-of-game).
     * @returns {boolean}
     */
    async restartCurrentLevel() {
        if (!this._everStarted && !this.canRestartLevel()) {
            return false;
        }
        // Clear the paused state before loading so startMusic() isn't blocked.
        this.soundManager.resume();
        const restarted = await this.loadLevel(this.levelId);
        if (!restarted) {
            return false;
        }
        this._everStarted = true;
        this.startGame();
        return true;
    }

    /**
     * @returns {boolean}
     */
    hasNextLevel() {
        const levels = getAllLevels();
        const idx = levels.findIndex((l) => l.id === this.levelId);
        return idx >= 0 && idx < levels.length - 1;
    }

    /**
     * @returns {boolean}
     */
    hasPreviousLevel() {
        const levels = getAllLevels();
        const idx = levels.findIndex((l) => l.id === this.levelId);
        return idx > 0;
    }

    /**
     * @returns {Promise<boolean>}
     */
    async advanceToNextLevel() {
        const levels = getAllLevels();
        const idx = levels.findIndex((l) => l.id === this.levelId);
        if (idx < 0 || idx >= levels.length - 1) {
            return false;
        }
        return this.loadLevel(levels[idx + 1].id);
    }

    /**
     * @returns {Promise<boolean>}
     */
    async retreatToPreviousLevel() {
        const levels = getAllLevels();
        const idx = levels.findIndex((l) => l.id === this.levelId);
        if (idx <= 0) {
            return false;
        }
        return this.loadLevel(levels[idx - 1].id);
    }

    /**
     * Build entitySystem + techTreeManager + scoreTracker for the current level.
     * Called once from the constructor and once per level load. Caller is
     * responsible for populating `this.locationGrid`, `this.destinationGrid`,
     * and `this.effectSystem` first; this method wires them into the new
     * entitySystem via initEntityModule(). Sound needs no wiring — everything
     * imports the shared `soundManager` from sound.js directly.
     */
    _setupEntityAndTechSystems() {
        this.entitySystem = new EntitySystem(this.effectSystem, this.eventFeed);
        this.entitySystem.breakoutTiles = this.breakoutTiles;
        this.entitySystem.breakoutTileSize = BREAKOUT_TILE_SIZE;
        this.entitySystem.breakoutTileDurability = BREAKOUT_COLLISIONS_TO_BREAK;
        this.entitySystem.onBreakoutTilePlaced = (tile) => this._ejectEntitiesFromTile(tile);
        this.entitySystem.onTutorialMessage = (text, color, speechBubble, worldX, worldY) => {
            this.tutorialMessages.push({ text, color, speechBubble: !!speechBubble, worldX, worldY, expiresAt: Date.now() + 6000 });
            // Tutorial messages are voiced but intentionally bypass the event feed
            // (the floating bubble is the visual; the feed is for gameplay events).
            this._speakDirect?.(text);
        };
        initEntityModule(this.locationGrid, this.destinationGrid, this.entitySystem, this.effectSystem);

        const techOptions = {};
        if (this.level?.techWhitelist) techOptions.techWhitelist = this.level.techWhitelist;
        if (this.level?.techScale != null) techOptions.techScale = this.level.techScale;
        this.techTreeManager = new TechTreeManager(this.entitySystem, techOptions);
        this.entitySystem.techTreeManager = this.techTreeManager;
        this.entitySystem.onFactionEliminated = () => this.soundManager.playSfx(Sfx.FACTION_ELIMINATED);

        this.scoreTracker = new ScoreTracker(this.levelId);
        this._lastScoreResult = null;
    }

    /**
     * Reset all per-level state and rebuild the world for `this.level`.
     * The ONLY reset path — called from the constructor and from loadLevel()
     * so the two sequences cannot drift (a field reset on reload but not on
     * first construction, or vice versa).
     *
     * Requires `this.level` and `this.eventFeed` to be in place. Does NOT
     * touch the renderer/UI — the constructor creates them after this runs;
     * loadLevel() re-wires them after.
     */
    _resetLevelState() {
        this.levelTimeLimitSec = this.level.timeLimitSec || 0;
        this.levelZoomScale = this.level.zoomScale || 1;
        this.levelElapsedSec = 0;
        this.gameState = 0;
        this._everStarted = false;
        /** True between game_started and its terminal event (ended/abandoned). */
        this._analyticsGameOpen = false;
        this.postGameSettleUntil = 0;
        /** @type {Set<number>} Factions whose defeat we have already announced. */
        this._defeatedAnnounced = new Set();
        /** @type {Set<number>} Factions currently in "nearly defeated" warning state. */
        this._factionWarned = new Set();
        this._needsInitialRender = true;
        // Dirty flag for non-simulation rendering. When the game isn't simulating,
        // we skip fg/ui redraws unless input events or state changes set this.
        this._renderDirty = true;
        this.lowFpsStreakSeconds = 0;
        this.renderFrameCounter = 0;
        this.pendingRenderTime = 0;
        this.breakoutTiles.clear();
        this.tutorialMessages = [];

        this.locationGrid = new GridSystem(this.width / this.gridSize, this.height / this.gridSize, this.gridSize);
        this.destinationGrid = new GridSystem(this.width / this.gridSize, this.height / this.gridSize, this.gridSize);
        this.soundManager.setWorldWidth(this.width);
        // ONE EffectSystem for the Game's whole lifetime: created on first
        // reset, cleared on later ones. Renderers register their effect
        // factories on it exactly once (in their constructor), so replacing
        // the instance here would silently orphan that registration.
        this.effectSystem ??= new EffectSystem();
        this.effectSystem.clear();
        this._setupEntityAndTechSystems();
        this.initializePowers();
    }

    /**
     * Populate the level's start state and bring up the AIs.
     * Shared by the constructor and loadLevel(). Runs after _resetLevelState();
     * in the constructor the UI is created in between (so loadSettings can
     * record AI choices before the AIs are built).
     */
    _startLevel() {
        // Spawners must exist before AI init.
        this.setupStartState(this.locationGrid.width, this.locationGrid.height);
        this.initializeAis();
        for (let a = 0; a < this.entitySystem.computerAis.length; a += 1) {
            if (this.entitySystem.computerAis[a] !== null) {
                this.entitySystem.computerAis[a].init();
            }
        }
    }

    /**
     * @param {string} levelId
     * @returns {Promise<boolean>}
     */
    async loadLevel(levelId) {
        const level = await getLevelById(levelId);
        if (!level) {
            return false;
        }
        this.trackAbandonment(level.id === this.levelId ? 'restart' : 'level_change');
        this.levelId = level.id;
        this.level = level;
        this._resetLevelState();
        this._startLevel();

        if (this.eventFeed) {
            this.eventFeed.clear();
        }
        // No renderer re-wiring: it reads gridSystem/entitySystem/effectSystem
        // live off this Game, and the EffectSystem instance (with the
        // renderer's factory registration) survives level loads.
        if (this.ui) {
            this.ui.setGameSize(this.locationGrid.width, this.locationGrid.height);
            if (this.ui.mouseListener) {
                this.ui.mouseListener.resize(this.ui.mouseListener.canvas);
            }
        }
        if (this.onLevelLoaded) this.onLevelLoaded(this.levelId);
        return true;
    }

    initializePowers() {
        const allPowers = createDefaultPowers();
        // Legacy fallback: if `powers` is omitted entirely, enable all powers.
        // If `powers` is provided (including []), respect it exactly.
        if (!this.level || !Array.isArray(this.level.powers)) {
            this.powers = allPowers;
            this.powerUseLimits = new Map();
            this.powerSecsPerCharge = new Map();
            this.powerMaxStoredUses = new Map();
        } else {
            const configuredIds = new Set(this.level.powers.map((p) => p.id));
            this.powers = allPowers.filter((p) => configuredIds.has(p.id));
            this.powerUseLimits = new Map();
            this.powerSecsPerCharge = new Map();
            this.powerMaxStoredUses = new Map();
            for (let i = 0; i < this.level.powers.length; i += 1) {
                const powerConfig = this.level.powers[i];
                const isLimited = typeof powerConfig.maxUses === 'number';
                const maxStoredUses = isLimited ?
                    (typeof powerConfig.maxStoredUses === 'number' ? powerConfig.maxStoredUses : powerConfig.maxUses) :
                    Infinity;
                const initialUses = isLimited ? powerConfig.maxUses : null;
                this.powerUseLimits.set(
                    powerConfig.id,
                    initialUses
                );
                this.powerMaxStoredUses.set(powerConfig.id, maxStoredUses);
                this.powerSecsPerCharge.set(powerConfig.id, Math.max(0, powerConfig.secsPerCharge || 0));
            }
        }

        // EMP is a persistent earned power-up (see powerup_inventory.js):
        // always slot 0, charges shared across levels and sessions, never
        // granted by level config.
        this.powers = this.powers.filter((p) => p.id !== 'emp');
        const empPower = allPowers.find((p) => p.id === 'emp');
        if (empPower) this.powers.unshift(empPower);
        if (this.level && this.level.grantEmpOnFirstEntry) tryGrantEmpOnEntry(this.levelId);
        this.powerUseLimits.set('emp', getEmpCharges());
        this.powerSecsPerCharge.delete('emp');
        this.powerMaxStoredUses.delete('emp');
        this._lastEmpAwarded = false;

        // EMP must never be the default selection — one stray tap would spend
        // an earned charge on a field-wide wipe. With no other powers, select
        // nothing (activateSelectedPowerUp no-ops on an unknown id).
        const defaultPower = this.powers.find((p) => p.id !== 'emp');
        this.selectedPowerUp = defaultPower ? defaultPower.id : '';
    }

    /* Setup playing field with spawners */
    setupStartState(width, height) {
        if (this.setupLevelSpawners()) {
            this.setupLevelEntities();
            Util.shuffleArray(this.entitySystem.units);
            // Initialize faction tech goals now that spawners exist
            this.techTreeManager.reevaluateAllGoals();
            return;
        }

        var x, y, neutralSpawners, s, blocked, attempts = 0,
            spawnerMinDistance = 150,
            spawnerMinDistanceFromEdge = 80,
            spawnUnitsPerNeutralSpawner = 100;

        // create neutral spawners, making sure it's odd
        neutralSpawners = Math.floor(Math.floor(Math.random() * 5 + 12) / 2) * 2 + 1;

        // y of first spawner
        y = Math.floor(Math.random() * (height - spawnerMinDistanceFromEdge * 2) +
            spawnerMinDistanceFromEdge);

        // faction 1 HQ
        this.createSpawner('hq', Faction.BLUE, spawnerMinDistanceFromEdge, y,
            spawnUnitsPerNeutralSpawner * neutralSpawners / 2);

        // faction 2 HQ
        this.createSpawner('hq', Faction.RED, width - spawnerMinDistanceFromEdge, height - y,
            spawnUnitsPerNeutralSpawner * neutralSpawners / 2);


        // if the number is odd, one spawner must be exactly in the middle
        if (neutralSpawners % 2 === 1) {
            s = this.createSpawner('spawner', Faction.NEUTRAL, width / 2, height / 2, 0);

            s.spawning.spawnPerSec = 30;
            s.capture.spawnOnCapture = 100;

            neutralSpawners -= 1;
        }

        while (neutralSpawners > 0) {
            x = Math.floor(Math.random() * (width / 2 - spawnerMinDistanceFromEdge) +
                spawnerMinDistanceFromEdge);
            y = Math.floor(Math.random() * (height - spawnerMinDistanceFromEdge * 2) +
                spawnerMinDistanceFromEdge);
            blocked = false;

            if (Util.distance(x, y, width - x, height - y) < spawnerMinDistance) {
                blocked = true;
            } else {
                for (s=0; s<this.entitySystem.spawners.length; s++) {
                    const spawner = this.entitySystem.spawners[s];
                    if (Util.distance(spawner.x, spawner.y, x, y) < spawnerMinDistance) {
                        blocked = true;
                        break;
                    }
                }
            }

            if (!blocked) {
                this.createSpawner('spawner', Faction.NEUTRAL, x, y, 0);
                this.createSpawner('spawner', Faction.NEUTRAL, width - x, height - y, 0);
                neutralSpawners -= 2;
            }
            
            // Safety: reduce min distance if we're stuck trying to place spawners
            // This prevents infinite loops in small game worlds
            if (attempts++ > 1000) {
                spawnerMinDistance *= 0.9;
                attempts = 0;
                if (spawnerMinDistance < 50) {
                    // Give up if distance gets too small
                    break;
                }
            }
        }

        Util.shuffleArray(this.entitySystem.units);
        
        // Initialize faction tech goals now that spawners exist
        this.techTreeManager.reevaluateAllGoals();
    }

    /**
     * Place non-spawner entities (derelict ships, pickups) from the unified entities array.
     */
    setupLevelEntities() {
        if (!this.level || !Array.isArray(this.level.entities)) {
            return;
        }
        const PICKUP_FACTORIES = { tech_boost: createTechBoostPickup };
        for (const cfg of this.level.entities) {
            const shipFactory = BONUS_SHIP_FACTORIES[cfg.type];
            if (shipFactory) {
                const faction = cfg.faction ?? 0;
                let ship;
                if (faction !== 0) {
                    // Spawn directly as a faction-owned unit (not claimable).
                    ship = shipFactory(faction, cfg.x, cfg.y);
                } else {
                    ship = createClaimable(cfg.x, cfg.y, { shipFactory });
                    if (cfg.orbit) {
                        ship.orbit = {
                            centerX: cfg.orbit.centerX,
                            centerY: cfg.orbit.centerY,
                            radius: cfg.orbit.radius,
                            angle: cfg.orbit.phaseRad ?? 0,
                            angularSpeed: cfg.orbit.angularSpeedRadPerSec ?? 0.5,
                            onlyWhenNeutral: cfg.orbit.onlyWhenNeutral !== undefined ? cfg.orbit.onlyWhenNeutral : true
                        };
                    }
                }
                ship.ensureVisual().facingAngle = Math.random() * Math.PI * 2;
                ship.init();
                this.entitySystem.registerEntity(ship);
                continue;
            }
            if (cfg.type === 'tech_gift') {
                const pickup = createTechGiftPickup(cfg.x, cfg.y, cfg.techId);
                pickup.init();
                this.entitySystem.registerEntity(pickup);
                continue;
            }
            if (cfg.type === 'text_trigger') {
                const pickup = createTextTriggerPickup(cfg.x, cfg.y, cfg.text ?? '', cfg.color ?? '#FFFFFF', cfg.speechBubble ?? false);
                pickup.init();
                this.entitySystem.registerEntity(pickup);
                continue;
            }
            const pickupFactory = PICKUP_FACTORIES[cfg.type];
            if (pickupFactory) {
                const pickup = pickupFactory(cfg.x, cfg.y);
                pickup.init();
                this.entitySystem.registerEntity(pickup);
            }
        }
    }

    /**
     * @returns {boolean}
     */
    setupLevelSpawners() {
        // If a level explicitly defines `spawners` (even as an empty array),
        // treat that as intentional authoring and do not fall back to random autogen.
        if (!this.level || !Array.isArray(this.level.spawners)) {
            return false;
        }
        for (let i = 0; i < this.level.spawners.length; i += 1) {
            const cfg = this.level.spawners[i];
            const spawnerType = cfg.type === 'hq' ? 'hq' : 'spawner';
            const initialUnits = cfg.initialUnits || 0;
            const isNeutral = cfg.faction === 0 || cfg.faction === Faction.NEUTRAL;
            const spawner = this.createSpawner(
                spawnerType, cfg.faction, cfg.x, cfg.y,
                isNeutral ? 0 : initialUnits
            );
            if (isNeutral && initialUnits > 0 && spawner.capture) {
                spawner.capture.spawnOnCapture = initialUnits;
            }
            if (cfg.orbit) {
                spawner.orbit = {
                    centerX: cfg.orbit.centerX,
                    centerY: cfg.orbit.centerY,
                    radius: cfg.orbit.radius,
                    angle: cfg.orbit.phaseRad ?? 0,
                    angularSpeed: cfg.orbit.angularSpeedRadPerSec ?? 0.4,
                    onlyWhenNeutral: cfg.orbit.onlyWhenNeutral !== undefined ? cfg.orbit.onlyWhenNeutral : true
                };
            }
            if (typeof cfg.spawnPerSec === 'number' && spawner.spawning) {
                spawner.spawning.spawnPerSec = cfg.spawnPerSec;
            }
            if (typeof cfg.size === 'number') {
                spawner.size = cfg.size;
            }

            // Scale capture effort with production rate so slower spawners are quicker to take.
            // 10 capture-points per spawn/sec: a 1/sec spawner needs 10 pts, an 8/sec needs 80.
            if (spawner.capture && spawner.spawning) {
                const CAPTURE_PTS_PER_SPAWN_RATE = 10;
                const newCapturePoints = spawner.spawning.spawnPerSec * CAPTURE_PTS_PER_SPAWN_RATE;
                // init() already set capturePointStatus = old capturePoints. Clamp it to
                // the new scale so capturePercent stays ≤ 1 and spawn rate isn't inflated.
                if (spawner.capture.capturePointStatus >= spawner.capture.capturePoints) {
                    spawner.capture.capturePointStatus = newCapturePoints;
                }
                spawner.capture.capturePoints = newCapturePoints;
            }
            if (cfg.planetArt) {
                const planet = PLANET_OPTIONS[cfg.planetArt];
                spawner.planetArt = planet ? planet.path : cfg.planetArt;
                spawner.planetScale = planet ? planet.scale : 1;
            }

            if (cfg.bonusShipType && BONUS_SHIP_FACTORIES[cfg.bonusShipType]) {
                spawner.bonusShipType = cfg.bonusShipType;
                spawner.bonusShipFactory = BONUS_SHIP_FACTORIES[cfg.bonusShipType];
                spawner.bonusShipPreview = spawner.bonusShipFactory(Faction.NEUTRAL, spawner.x, spawner.y, {});
                spawner.bonusShipPreview.visual.facingAngle = -Math.PI / 4;
            }

            if (spawner.capture) {
                spawner.capture.captureRadius = computeCaptureRadius(spawner.size);
            }

            // Spawn destination radius in world units — matches the visual planet circle radius.
            spawner.spawnRadius = spawner.size / 2;

        }

        return true;
    }

    /**
     * @param {'hq'|'spawner'} type
     * @param {number} faction
     * @param {number} x
     * @param {number} y
     * @param {number} spawnUnits - Number of starting units to spawn
     * @returns {Spawner}
     */
    createSpawner(type, faction, x, y, spawnUnits) {
        var spawner = type === 'hq' ?
            createSpawnerHQ(faction, x, y) :
            createSpawner(faction, x, y);
        // Defer initial unit spawning until the game actually starts so units
        // don't appear on the pre-game preview.
        if (spawnUnits > 0) {
            spawner._pendingInitialUnits = spawnUnits;
        }
        spawner.init();
        this.entitySystem.registerEntity(spawner);
        return spawner;
    }

    /* Mouse command processing - removed old RTS controls */

    /**
     * Iterate every entity within circular range of a point.
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @param {(entity: any) => void} callback
     */
    forEachEntityInRange(x, y, radius, callback) {
        // Snapshot entities before calling any callback: getSectorsInRange writes
        // into a shared module-level buffer, so re-entrant calls (e.g. from
        // teleportEntity) would overwrite it mid-iteration and skip entities.
        const sectors = this.locationGrid.getSectorsInRange(x, y, radius);
        const candidates = [];
        for (let s = 0; s < sectors.length; s += 1) {
            const sector = sectors[s];
            for (let i = 0; i < sector.entities.length; i += 1) {
                const entity = sector.entities[i];
                if (Util.distance(entity.x, entity.y, x, y) <= radius) {
                    candidates.push(entity);
                }
            }
        }
        for (let i = 0; i < candidates.length; i += 1) {
            callback(candidates[i]);
        }
    }

    /**
     * @returns {Power[]}
     */
    getPowers() {
        return this.powers;
    }

    /**
     * @param {string} powerId
     * @returns {Power|undefined}
     */
    getPowerById(powerId) {
        return this.powers.find((power) => power.id === powerId);
    }

    /**
     * @param {string} powerUp
     */
    setSelectedPowerUp(powerUp) {
        if (!this.getPowerById(powerUp)) return;
        if (powerUp === 'emp') {
            const uses = this.getPowerUsesRemaining('emp');
            if (uses !== null && uses < 1) return;
            if (this.selectedPowerUp !== 'emp') this._preEmpSelection = this.selectedPowerUp;
        }
        this.selectedPowerUp = powerUp;
    }

    /**
     * @returns {string}
     */
    getSelectedPowerUp() {
        return this.selectedPowerUp;
    }

    /**
     * @returns {Power|undefined}
     */
    getSelectedPower() {
        return this.getPowerById(this.selectedPowerUp);
    }

    /**
     * Activate currently selected intervention at a coordinate.
     * @param {number} x
     * @param {number} y
     */
    activateSelectedPowerUp(x, y) {
        const selectedPower = this.getSelectedPower();
        if (selectedPower) {
            const usesRemaining = this.getPowerUsesRemaining(selectedPower.id);
            if (usesRemaining !== null && usesRemaining < 1) {
                return;
            }
            selectedPower.activate(this, x, y);
            if (!selectedPower.dragActivates) this.soundManager.playSfx(selectedPower.sfxId, { x });
            if (usesRemaining !== null) {
                this.powerUseLimits.set(selectedPower.id, Math.max(0, usesRemaining - 1));
            }
            if (selectedPower.id === 'emp') {
                // Persist immediately so restarting the level cannot refund it,
                // then auto-disarm back to the previously selected power.
                consumeEmpCharge();
                const prev = this._preEmpSelection;
                const fallback = this.powers.find((p) => p.id !== 'emp');
                if (prev && prev !== 'emp' && this.getPowerById(prev)) {
                    this.selectedPowerUp = prev;
                } else if (fallback) {
                    this.selectedPowerUp = fallback.id;
                }
            }
        }
    }

    /**
     * @returns {any}
     */
    getLevel() {
        return this.level;
    }

    /**
     * @returns {number}
     */
    getLevelTimeLimitSec() {
        return this.levelTimeLimitSec;
    }

    /**
     * @returns {number}
     */
    getLevelZoomScale() {
        return this.levelZoomScale || 1;
    }

    /**
     * @returns {number}
     */
    getLevelTimeRemainingSec() {
        if (this.levelTimeLimitSec <= 0) {
            return Infinity;
        }
        return Math.max(0, this.levelTimeLimitSec - this.levelElapsedSec);
    }

    /**
     * @param {string} powerId
     * @returns {number|null}
     */
    getPowerUsesRemaining(powerId) {
        if (!this.powerUseLimits || !this.powerUseLimits.has(powerId)) {
            return null;
        }
        return this.powerUseLimits.get(powerId);
    }

    /**
     * @param {string} powerId
     * @returns {number|null}
     */
    getPowerUsesDisplay(powerId) {
        const usesRemaining = this.getPowerUsesRemaining(powerId);
        if (usesRemaining === null) {
            return null;
        }
        return Math.floor(Math.max(0, usesRemaining));
    }

    /**
     * Returns 0..1 recharge progress toward the next use, or null if no recharge mechanic.
     * 0 = just used / empty, 1 = fully charged.
     * @param {string} powerId
     * @returns {number|null}
     */
    getPowerRechargeProgress(powerId) {
        const usesRemaining = this.getPowerUsesRemaining(powerId);
        if (usesRemaining === null) return null;
        const secsPerCharge = this.powerSecsPerCharge && this.powerSecsPerCharge.get(powerId);
        if (!secsPerCharge) return null;
        // Fractional part = progress toward the next charge
        return usesRemaining % 1;
    }

    /**
     * @param {number} timePassed
     */
    updatePowerRecharges(timePassed) {
        if (!this.powerUseLimits || this.powerUseLimits.size === 0) {
            return;
        }
        this.powerUseLimits.forEach((usesRemaining, powerId) => {
            if (usesRemaining === null) {
                return;
            }
            const secsPerCharge = this.powerSecsPerCharge.get(powerId) || 0;
            if (secsPerCharge <= 0) {
                return;
            }
            const maxStored = this.powerMaxStoredUses.get(powerId);
            const maxAllowed = Number.isFinite(maxStored) ? maxStored : usesRemaining;
            const recharged = Math.min(maxAllowed, usesRemaining + (timePassed / secsPerCharge));
            this.powerUseLimits.set(powerId, recharged);
        });
    }

    /* UI actions */

    /**
     * @param {number} factor
     * @param {boolean} [mobile] - true when the portrait/mobile layout is active.
     */
    setScaleFactor(factor, mobile) {
        this.renderer.setScaleFactor(factor, mobile);
    }

    /* Retrieval methods used by other modules */

    /**
     * @returns {number}
     */
    getGameState() {
        return this.gameState;
    }

    /**
     * Did the player meet the level objective?
     *
     * Balance maintained through the time limit is a win; one faction
     * eliminating the others early is a loss. The board ending with no faction
     * in play reads as state `100 + NEUTRAL` for one tick before
     * handleLossRestart() reclassifies it as a level completion, so it is
     * counted here too — otherwise _saveScore(), which runs first, would log a
     * loss for a level the player is about to be shown as complete.
     * @returns {boolean}
     */
    _isLevelWon() {
        return this.gameState === GAME_STATE_LEVEL_COMPLETE
            || this.gameState === 100 + Faction.NEUTRAL;
    }

    /**
     * Close an open analytics session that never reached a win or loss, so
     * every game_started gets exactly one terminal event. No-op when no game
     * is open, so it is safe to call from any exit path.
     * @param {'restart'|'level_change'|'page_exit'} reason
     */
    trackAbandonment(reason) {
        if (!this._analyticsGameOpen) return;
        this._analyticsGameOpen = false;
        trackGameAbandoned({
            levelId:     this.levelId,
            reason,
            score:       this.scoreTracker ? this.scoreTracker.getDisplayScore() : 0,
            durationSec: this.time,
        });
    }

    _saveScore() {
        if (!this.headless && this.scoreTracker && this.level) {
            this._lastScoreResult = this.scoreTracker.saveBestScore(this.level.scoreStars);
            const won = this._isLevelWon();
            const stars = this.scoreTracker.getStars(this.level.scoreStars);
            this._lastEmpAwarded = won && stars === 3 && tryAwardEmp(this.levelId);
            // Paired with the game_started emitted by startGame().
            this._analyticsGameOpen = false;
            trackGameEnded({
                levelId:     this.levelId,
                won,
                score:       this.scoreTracker.getDisplayScore(),
                stars,
                durationSec: this.time,
                fps:         this.measuredFps ?? this.lastFps,
                peakGapMs:   this.peakFrameGapMs ?? 0,
                jankCount:   this.jankFrameCount ?? 0,
            });
        }
    }

    /** @returns {import('./scoring.js').ScoreTracker} */
    getScoreTracker() {
        return this.scoreTracker;
    }

    /**
     * @returns {{ isNewBest: boolean, bestScore: number, stars: number }|null}
     */
    getLastScoreResult() {
        return this._lastScoreResult || null;
    }

    /**
     * True when the run that just ended earned a first-time 3-star EMP charge.
     * @returns {boolean}
     */
    getLastEmpAwarded() {
        return !!this._lastEmpAwarded;
    }

    /**
     * @returns {number}
     */
    getFps() {
        return this.lastFps;
    }

    /**
     * @returns {number}
     */
    getTargetFps() {
        return this.targetFps;
    }

    /**
     * @param {number} fps
     */
    setTargetFps(fps) {
        this.targetFps = fps;
        if (this._renderDirty === false) this._renderDirty = true;
    }

    /**
     * Returns the device pixel ratio to use for canvas rendering, taking the
     * current graphics quality mode and adaptive state into account.
     * @returns {number}
     */
    getEffectiveDpr() {
        if (typeof devicePixelRatio === 'undefined') return 1;
        const nativeDpr = Math.min(devicePixelRatio, 3);
        if (this.graphicsQuality === 'high') return nativeDpr;
        if (this.graphicsQuality === 'low')  return 1;
        return Math.max(1, nativeDpr - this._adaptiveDprSteps);
    }

    /**
     * Set graphics quality and trigger a canvas resize to apply the new DPR.
     * @param {'auto'|'high'|'low'} mode
     */
    setGraphicsQuality(mode) {
        this.graphicsQuality = mode;
        this._adaptiveDprSteps = 0;
        this._lowDprFrameWindows = 0;
        this._highDprFrameWindows = 0;
        this._triggerCanvasResize();
    }

    /**
     * Trigger a canvas resize using the renderer's cached canvas reference.
     * Safe to call from the game loop — no getElementById needed.
     */
    _triggerCanvasResize() {
        const canvas = this.renderer?.inputCanvas;
        if (canvas && this.ui && this.ui.mouseListener) {
            this.ui.mouseListener.resize(canvas);
        }
    }

    /**
     * Returns available TTS voices as an array of {id, label} objects.
     * On Android uses the native bridge; on web uses speechSynthesis.
     * @returns {{id: string, label: string}[]}
     */
    getAvailableVoices() {
        if (typeof window === 'undefined') return [];
        if (window.AndroidTTS?.getVoicesJson) {
            try {
                const voices = JSON.parse(window.AndroidTTS.getVoicesJson());
                // Supports both old plain-string array and new {id, label} object array.
                return voices.map((v) => typeof v === 'string' ? { id: v, label: v } : { id: v.id, label: v.label });
            } catch (_) { return []; }
        }
        if (window.speechSynthesis) {
            return window.speechSynthesis.getVoices()
                .filter(v => v.lang.startsWith('en'))
                .map(v => ({ id: v.name, label: v.name }));
        }
        return [];
    }

    /**
     * Set the active TTS voice by name and speak a confirmation phrase.
     * Pass null to restore auto-selection.
     * @param {string|null} name
     */
    setVoiceName(name) {
        this.voiceName = name;
        this._speakDirect?.('Welcome to the Swarm');
    }

    /**
     * @param {number} mode
     */
    setSimulationSpeedMode(mode) {
        if (!SIMULATION_SPEED_MODES.includes(mode)) {
            return;
        }
        this.simulationSpeedMode = mode;
        this.renderEveryNFrames = mode < 1 ? 1 : mode;
        this.renderFrameCounter = 0;
        this.pendingRenderTime = 0;
    }

    cycleSimulationSpeedMode() {
        const idx = SIMULATION_SPEED_MODES.indexOf(this.simulationSpeedMode);
        const next = (idx + 1) % SIMULATION_SPEED_MODES.length;
        this.setSimulationSpeedMode(SIMULATION_SPEED_MODES[next]);
    }

    stepSimulationSpeedFaster() {
        const idx = SIMULATION_SPEED_MODES.indexOf(this.simulationSpeedMode);
        if (idx < SIMULATION_SPEED_MODES.length - 1) {
            this.setSimulationSpeedMode(SIMULATION_SPEED_MODES[idx + 1]);
        }
    }

    stepSimulationSpeedSlower() {
        const idx = SIMULATION_SPEED_MODES.indexOf(this.simulationSpeedMode);
        if (idx > 0) {
            this.setSimulationSpeedMode(SIMULATION_SPEED_MODES[idx - 1]);
        }
    }

    /**
     * @returns {number}
     */
    getSimulationSpeedMode() {
        return this.simulationSpeedMode;
    }

    /**
     * @returns {boolean}
     */
    isAiDebugMode() {
        return this.aiDebugMode === true;
    }

    toggleAiDebugMode() {
        this.aiDebugMode = !this.aiDebugMode;
    }

    getBreakoutTileSize() {
        return BREAKOUT_TILE_SIZE;
    }

    getBreakoutTiles() {
        return Array.from(this.breakoutTiles.values());
    }

    beginSelectedPowerDrag() {
        const selectedPower = this.getSelectedPower();
        if (selectedPower && selectedPower.beginDrag) {
            selectedPower.beginDrag(this);
        }
        if (selectedPower && selectedPower.sfxId) this.soundManager.playSfx(selectedPower.sfxId);
    }

    endSelectedPowerDrag() {
        const selectedPower = this.getSelectedPower();
        if (selectedPower && selectedPower.endDrag) {
            selectedPower.endDrag(this);
        }
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {boolean}
     */
    addBreakoutTileFromCursor(x, y) {
        const tx = Math.floor(x / BREAKOUT_TILE_SIZE) * BREAKOUT_TILE_SIZE;
        const ty = Math.floor(y / BREAKOUT_TILE_SIZE) * BREAKOUT_TILE_SIZE;
        const existing = this.breakoutTiles.get(`${tx},${ty}`);
        if (existing && existing.hitCount === 0) return false;
        this.entitySystem.addBreakoutTile(x, y, 0, BREAKOUT_COLLISIONS_TO_BREAK);
        return true;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {{x: number, y: number, hitCount: number, hitsToBreak: number}|null}
     */
    getBreakoutTileAtCursor(x, y) {
        const tx = Math.floor(x / BREAKOUT_TILE_SIZE) * BREAKOUT_TILE_SIZE;
        const ty = Math.floor(y / BREAKOUT_TILE_SIZE) * BREAKOUT_TILE_SIZE;
        const key = `${tx},${ty}`;
        return this.breakoutTiles.get(key) || null;
    }

    /**
     * @param {Entity} entity
     * @returns {boolean}
     */
    resolveBreakoutBlockCollision(entity) {
        if (!entity || !entity.movement || entity.isDestroyed()) {
            return false;
        }
        const tile = this.getBreakoutTileAtCursor(entity.x, entity.y);
        if (!tile) {
            return false;
        }

        // Faction-owned tiles let same-faction units pass through
        if (tile.faction !== 0 && entity.faction === tile.faction) {
            return false;
        }

        tile.hitCount += 1;
        if (tile.hitCount >= tile.hitsToBreak) {
            this.breakoutTiles.delete(`${tile.x},${tile.y}`);
            this.soundManager.playSfx(Sfx.BREAKOUT_BREAK);
        }
        this.reverseEntityDirectionFromBreakoutCollision(entity, tile);
        return true;
    }

    /**
     * @param {Entity} entity
     * @param {{x: number, y: number}} tile - The breakout tile that was hit
     */
    reverseEntityDirectionFromBreakoutCollision(entity, tile) {
        const prevVelX = entity.velX;
        const prevVelY = entity.velY;
        const speed = Math.sqrt(prevVelX * prevVelX + prevVelY * prevVelY);
        const EJECT_SPEED_THRESHOLD = 0.5;

        if (speed < EJECT_SPEED_THRESHOLD) {
            // Entity is stationary or nearly so — eject it outward past all
            // adjacent blocks so it doesn't just bounce between neighbors.
            const tileCenterX = tile.x + BREAKOUT_TILE_SIZE / 2;
            const tileCenterY = tile.y + BREAKOUT_TILE_SIZE / 2;
            let dirX = entity.x - tileCenterX;
            let dirY = entity.y - tileCenterY;
            const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);

            if (dirLen < 0.01) {
                const angle = Math.random() * Math.PI * 2;
                dirX = Math.cos(angle);
                dirY = Math.sin(angle);
            } else {
                dirX /= dirLen;
                dirY /= dirLen;
            }

            // Walk outward one tile at a time until we find a spot with no
            // hostile block (friendly faction tiles don't block us)
            let ejectX = tileCenterX;
            let ejectY = tileCenterY;
            for (let step = 1; step <= 20; step += 1) {
                ejectX = tileCenterX + dirX * BREAKOUT_TILE_SIZE * step;
                ejectY = tileCenterY + dirY * BREAKOUT_TILE_SIZE * step;
                const checkTile = this.getBreakoutTileAtCursor(ejectX, ejectY);
                if (!checkTile) break;
                if (checkTile.faction !== 0 && entity.faction === checkTile.faction) break;
            }

            entity.x = ejectX;
            entity.y = ejectY;

            const kickSpeed = 2.0;
            entity.velX = dirX * kickSpeed;
            entity.velY = dirY * kickSpeed;
            entity.setDestination(entity.x + dirX * 60, entity.y + dirY * 60);
        } else {
            // Normal bounce: reverse with noise
            const reversedVelX = -prevVelX;
            const reversedVelY = -prevVelY;
            const baseAngle = Math.atan2(reversedVelY, reversedVelX);
            const noise = (Math.random() * 2 - 1) * BREAKOUT_BOUNCE_NOISE_RADIANS;
            const noisyAngle = baseAngle + noise;
            entity.velX = Math.cos(noisyAngle) * speed;
            entity.velY = Math.sin(noisyAngle) * speed;

            const dx = entity.destX - entity.x;
            const dy = entity.destY - entity.y;
            if (Math.abs(dx) > 0.0001 || Math.abs(dy) > 0.0001) {
                entity.setDestination(entity.x - dx, entity.y - dy);
            } else {
                entity.setDestination(entity.x - prevVelX * 30, entity.y - prevVelY * 30);
            }
        }

        entity.aiLockedUntil = Math.max(entity.aiLockedUntil || 0, entity.time + BREAKOUT_AI_LOCK_SECONDS);
    }

    /**
     * Eject all entities that overlap a newly placed breakout tile, without dealing damage.
     * @param {{x: number, y: number}} tile
     */
    _ejectEntitiesFromTile(tile) {
        const ts = BREAKOUT_TILE_SIZE;
        const tileCenterX = tile.x + ts / 2;
        const tileCenterY = tile.y + ts / 2;
        const searchRadius = ts / 2 + 24;
        const sectors = this.locationGrid.getSectorsInRange(tileCenterX, tileCenterY, searchRadius);
        for (let s = 0; s < sectors.length; s++) {
            const entities = sectors[s].entities;
            for (let i = 0; i < entities.length; i++) {
                const entity = entities[i];
                if (entity.health?.isDestroyed()) continue;
                // Planets (spawners) are immovable — only eject combat units and derelicts.
                if (entity.type === 'spawner' || entity.type === 'spawner_hq') continue;
                if (tile.faction !== 0 && entity.faction === tile.faction) continue;
                const halfSize = ts / 2 + (entity.size || 0);
                if (Math.abs(entity.x - tileCenterX) < halfSize && Math.abs(entity.y - tileCenterY) < halfSize) {
                    this.reverseEntityDirectionFromBreakoutCollision(entity, tile);
                }
            }
        }
    }

    /**
     * Toggle the performance overlay display
     */
    togglePerformanceOverlay() {
        if (this.performanceOverlay) {
            this.performanceOverlay.toggleVisibility();
        }
    }

    /**
     * Toggle the event feed display
     */
    toggleEventFeed() {
        if (this.eventFeed) {
            this.eventFeed.toggleVisibility();
        }
    }

    /**
     * Switch between renderer backends live without a page reload.
     * Destroys the current renderer, creates the new one, and re-syncs the
     * scale factor so the new renderer is immediately ready to paint.
     * @param {'webgl'|'canvas2d'} type
     */
    switchRenderer(type) {
        if (!this.renderer || !this.ui) return;
        // Check the actual live renderer type via the interface property —
        // not storage (unavailable in private mode) and not instanceof.
        const current = this.renderer.type;
        if (type === current) return;
        // Persist preference.
        try {
            if (type === 'canvas2d') {
                getStorage().setItem('rendererBackend', 'canvas2d');
            } else {
                getStorage().removeItem('rendererBackend'); // webgl is default; no stored value needed
            }
        } catch { /* storage unavailable */ }
        this.rebuildRenderer(type);
    }

    /**
     * Tear down the current renderer and build a fresh one of the given type.
     * Unlike switchRenderer this works for the SAME type too — used to recover
     * from WebGL context loss, where the old context is dead and only a full
     * rebuild (new canvas, new Pixi app, re-rasterized textures) helps.
     * @param {'webgl'|'canvas2d'} type
     */
    rebuildRenderer(type) {
        if (!this.renderer || !this.ui) return;
        // Tear down old renderer (WebGL backend removes its GL canvas from DOM in destroy).
        this.renderer.destroy?.();
        // Re-use the same canvas elements.
        const canvases = {
            bg:      document.getElementById('bgCanvas'),
            fg:      document.getElementById('fgCanvas'),
            ui:      document.getElementById('uiCanvas'),
            effects: document.getElementById('effectsCanvas'),
        };
        this.renderer = createRenderer(
            canvases, this, this.ui, this.eventFeed, this.performanceOverlay,
            type
        );
        // Sync transform so the new renderer draws correctly from the first frame.
        // resize() treats its argument as the game canvas, so hand it the same
        // canvas _triggerCanvasResize() does — the renderer's own input canvas.
        this.ui.mouseListener?.resize?.(this.renderer.inputCanvas ?? canvases.fg);
        this.markRenderDirty();
        // Some backends initialise asynchronously.  Once ready, set
        // _needsInitialRender so renderBackground + renderGameLayer are called
        // even while paused (markRenderDirty alone only triggers renderUiLayer),
        // and hold the world dirty for a short settling window so a first frame
        // the browser drops can't leave the new backend showing nothing.
        this.renderer.whenReady().then(() => {
            this._needsInitialRender = true;
            this._worldRedrawFrames = RENDERER_REBUILD_REDRAW_FRAMES;
            this.markRenderDirty();
        }).catch(() => {});
    }

    /**
     * Called by the WebGL backend when its async Pixi init fails — the
     * browser would not hand out a GL context, or the WebGL chunk could not
     * load. There is no second chance worth taking: a device that refused
     * once refuses again, so fall back to Canvas2D and persist it (the
     * Settings panel still offers WebGL if the player wants to retry).
     * @param {object} renderer the backend that failed, so a stale one
     *   (already replaced by a rebuild) cannot switch the live one away.
     */
    handleWebGLInitFailed(renderer) {
        if (!this.renderer || this.renderer !== renderer || renderer.type !== 'webgl') return;
        console.warn('[Renderer] WebGL failed to initialise — falling back to Canvas2D.');
        this.switchRenderer('canvas2d');
    }

    /**
     * Called by the WebGL backend when its GL context is lost (GPU reset,
     * background-tab eviction on mobile). Without this the game stays a black
     * screen. Rebuild once for a fresh context; if contexts keep dying the
     * device can't sustain WebGL, so fall back to Canvas2D — persisted, such
     * that the next page load also starts on the backend that works.
     */
    handleContextLost() {
        if (!this.renderer || this.renderer.type !== 'webgl') return;
        const now = Date.now();
        const repeated = this._lastContextLostAt != null && now - this._lastContextLostAt < 60000;
        this._lastContextLostAt = now;
        if (repeated) {
            console.warn('[Renderer] WebGL context lost twice within a minute — falling back to Canvas2D.');
            this.switchRenderer('canvas2d');
        } else {
            console.warn('[Renderer] WebGL context lost — rebuilding renderer.');
            this.rebuildRenderer('webgl');
        }
    }

    /**
     * Toggle a render layer on/off by its 0-based index into renderer.layerDefs.
     * Only meaningful for backends that expose layerDefs (e.g. WebGL).
     * @param {number} idx
     */
    toggleRenderLayer(idx) {
        if (!this.renderer?.toggleLayer) return;
        const def = this.renderer.layerDefs?.[idx];
        if (def) this.renderer.toggleLayer(def.key);
        this.markRenderDirty();
    }

    /**
     * Returns current layer visibility states for the perf overlay.
     * @returns {Array<{label: string, visible: boolean}>|null}
     */
    getRenderLayerStates() {
        return this.renderer?.getLayerStates?.() ?? null;
    }
}

export { Game };

